import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "../components/SEO";
import { getProductBySlug, getCategoryBySlug } from "../data/products";
import { IconCheck, IconArrowLeft, IconWhatsapp } from "../components/Icons";
import NotFound from "./NotFound";

const WHATSAPP_NUMBER = "905431712923";

function renderDescription(description: string) {
  const lines = description.split("\n");

  type Module = { title: string; items: string[] };
  const modules: Module[] = [];
  const introLines: string[] = [];
  const outroLines: string[] = [];
  let currentModule: Module | null = null;
  let modulesSectionStarted = false;
  let modulesSectionEnded = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const isModuleTitle =
      trimmed.endsWith("Module") ||
      trimmed.endsWith("Module:") ||
      (/^(Heart|Lung|Cardiac|Pulmonary)\s+\w+/.test(trimmed) && !trimmed.startsWith("•"));

    const isBullet = trimmed.startsWith("•");

    if (isModuleTitle && !modulesSectionEnded) {
      modulesSectionStarted = true;
      currentModule = { title: trimmed.replace(/:$/, ""), items: [] };
      modules.push(currentModule);
    } else if (isBullet && currentModule) {
      currentModule.items.push(trimmed.replace(/^•\s*/, ""));
    } else if (modulesSectionStarted && !isBullet && !isModuleTitle) {
      modulesSectionEnded = true;
      currentModule = null;
      outroLines.push(trimmed);
    } else if (!modulesSectionStarted) {
      introLines.push(trimmed);
    } else if (modulesSectionEnded) {
      outroLines.push(trimmed);
    }
  }

  return (
    <div className="space-y-6 text-muted text-lg leading-relaxed">
      {introLines.map((line, i) => (
        <p key={i}>{line}</p>
      ))}

      {modules.length > 0 && (
        <div className={`grid gap-6 ${modules.length >= 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
          {modules.map((mod, i) => (
            <div key={i} className="bg-white rounded-2xl border border-border p-6">
              <h4 className="text-base font-bold text-primary mb-4 pb-2 border-b border-border">
                {mod.title}
              </h4>
              <ul className="space-y-2">
                {mod.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-accent/10 rounded-full flex items-center justify-center mt-0.5">
                      <IconCheck className="text-accent" size={11} />
                    </span>
                    <span className="text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {outroLines.map((line, i) => (
        <p key={i}>{line}</p>
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const product = slug ? getProductBySlug(slug) : undefined;

  const [selectedImage, setSelectedImage] = useState(0);
  const [descLang, setDescLang] = useState<"en" | "ko">("en");

  if (!product) return <NotFound />;

  const category = getCategoryBySlug(product.categorySlug);

  const whatsappMessage = encodeURIComponent(
    `Hello, I would like to get more information about the "${product.name}" product.`
  );
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;

  return (
    <>
      <SEO
        title={product.name}
        description={product.shortDescription}
        canonical={`https://haptone.com/product/${product.slug}`}
        type="product"
      />

      <section className="pt-24 lg:pt-28 pb-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-muted">
            <Link to="/" className="hover:text-secondary transition-colors">
              Home
            </Link>
            <span>/</span>
            {category && (
              <>
                <Link
                  to={`/category/${category.slug}`}
                  className="hover:text-secondary transition-colors"
                >
                  {category.name}
                </Link>
                <span>/</span>
              </>
            )}
            <span className="text-primary font-medium truncate">
              {product.name}
            </span>
          </nav>
        </div>
      </section>

      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div className="space-y-4">
              <div className="aspect-square rounded-2xl overflow-hidden bg-gray-50">
                <img
                  src={product.gallery[selectedImage] || product.image}
                  alt={product.name}
                  className="w-full h-full object-contain p-6"
                />
              </div>
              {product.gallery.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {product.gallery.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`aspect-square rounded-xl overflow-hidden bg-gray-50 transition-all duration-200 ${
                        selectedImage === i
                          ? "ring-2 ring-secondary ring-offset-2"
                          : "hover:ring-2 hover:ring-gray-300 hover:ring-offset-1 opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${i + 1}`}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain p-2"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <span className="text-accent text-sm font-semibold uppercase tracking-widest">
                {product.category}
              </span>

              <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-primary leading-tight">
                {product.name}
              </h1>

              <p className="mt-6 text-muted text-lg leading-relaxed">
                {product.shortDescription}
              </p>

              <div className="mt-8">
                <h2 className="text-lg font-semibold text-primary mb-4">
                  Key Features
                </h2>
                <ul className="space-y-3">
                  {product.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-accent/10 rounded-full flex items-center justify-center mt-0.5">
                        <IconCheck className="text-accent" size={14} />
                      </span>
                      <span className="text-muted leading-relaxed">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 flex flex-row flex-wrap gap-4">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold px-6 py-4 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[#25D366]/30 text-lg"
                >
                  <IconWhatsapp size={24} />
                  Order via WhatsApp
                </a>
                <Link
                  to={`/category/${product.categorySlug}`}
                  className="inline-flex items-center justify-center gap-2 border-2 border-border text-primary font-semibold px-6 py-4 rounded-xl hover:bg-light transition-all duration-300"
                >
                  <IconArrowLeft size={18} />
                  Back to {product.category}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary">
                Product Description
              </h2>
              {product.descriptionKo && (
                <div className="flex rounded-xl overflow-hidden border border-border text-sm font-semibold">
                  <button
                    onClick={() => setDescLang("en")}
                    className={`px-5 py-2 transition-colors duration-200 ${
                      descLang === "en"
                        ? "bg-secondary text-white"
                        : "bg-white text-muted hover:bg-light"
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => setDescLang("ko")}
                    className={`px-5 py-2 transition-colors duration-200 ${
                      descLang === "ko"
                        ? "bg-secondary text-white"
                        : "bg-white text-muted hover:bg-light"
                    }`}
                  >
                    한국어
                  </button>
                </div>
              )}
            </div>
            <div className="prose prose-lg max-w-none">
              {renderDescription(
                product.descriptionKo && descLang === "ko"
                  ? product.descriptionKo
                  : product.description
              )}
            </div>
          </div>
        </div>
      </section>

      {category && category.products.filter((p) => p.id !== product.id).length > 0 && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-10">
              Related Products
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {category.products
                .filter((p) => p.id !== product.id)
                .slice(0, 3)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/product/${related.slug}`}
                    className="group bg-white border border-border rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-secondary/10 transition-all duration-500 hover:-translate-y-1"
                  >
                    <div className="relative aspect-square overflow-hidden bg-gray-50">
                      <img
                        src={related.image}
                        alt={related.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain p-6 transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-primary group-hover:text-secondary transition-colors duration-200">
                        {related.name}
                      </h3>
                      <p className="mt-2 text-muted text-sm leading-relaxed line-clamp-2">
                        {related.shortDescription}
                      </p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
